// @flow
// $FlowFixMe
import React, { SyntheticKeyboardEvent } from 'react';
import { map, omit, isFunction, isEqual } from 'lodash';
import CurrencyInput from './CurrencyInput';
import type { Node } from 'react';
import { wrapValue, isPtBR, ignoreAutofillProps, shouldAutofocus } from '@helpers/helpers';
import { FormInput } from '../../blocks';
import './Input.scss';

type Props = {
  name?: string, // to-do change name to required
  autofocus?: boolean,
  mobileAutofocus?: boolean,
  autoComplete?: string,
  className?: string,
  disabled?: boolean,
  error?: string,
  hint?: string,
  id?: string,
  inputRenderer?: Array<{
    // reserved
    innerCssClasses?: string,
    node: Node,
    // classic input props
    className?: string,
    name?: string,
    id?: string,
  }>,
  required?: boolean,
  label?: string,
  labelClassName?: string,
  optional?: boolean,
  placeholder?: string,
  type?: string,
  value?: any,
  options?: Array<any>,
  withoutBorder?: boolean,
  readOnly?: boolean,
  onBlur?: Function,
  onChange?: Function,
  onCheck?: Function,
  onClick?: Function,
  onKeyUp?: Function,
  unit?: string,
  inputMode?: string,
  defaultValue?: string,
  forceDisabled?: boolean,
  uncontrolled?: boolean,
};

// Money fields read like the lists in Brazil: R$ 1.300,00. Only the text changes: the form gets
// the number, as before. Spanish keeps 1,300.00, the way Mexico, Peru, Panama and the Dominican
// Republic write it.
const currencyFormat = () => (isPtBR(window.i18n.language)
  ? { prefix: 'R$ ', decimalSeparator: ',', thousandSeparator: '.' }
  : {});

type HTMLInputElement = Object;
export default class Input extends React.Component<Props> {
  // No focus() of its own on mount: React's autoFocus below already focuses, following the web-only rule.
  // A mount focus that ignored the rule focused a modal's first field in the apps too, and Modal (which then
  // blurred anything focused on its mount) blurred it at once, marking it touched: Novo Paciente opened with
  // "Informe um nome" in red. Modal now keeps a field inside it that focused itself (mobileAutofocus).

  shouldComponentUpdate(nextProps: Props) {
    return !isEqual(this.props, nextProps);
  }

  renderCurrencyInput = (autofocus: ?boolean) => {
    const {
      value, defaultValue, uncontrolled, disabled, forceDisabled, readOnly,
    } = this.props;
    return (
      <CurrencyInput
        allowEmpty
        {...currencyFormat()}
        inputType="tel"
        {...wrapValue(defaultValue, value, uncontrolled)}
        onBlur={this.onBlur.bind(this)}
        onClick={this.handleInputClick}
        onKeyUp={this.onKeyUp.bind(this)}
        onChangeEvent={this.handleOnChangeCurrency}
        disabled={disabled || forceDisabled}
        readOnly={readOnly}
        autoFocus={autofocus}
      />
    );
  };

  render() {
    const {
      value, id, label,
      className, labelClassName, optional,
      placeholder, disabled, type,
      error, required, hint, forceDisabled,
      name, inputRenderer, withoutBorder,
      autoComplete, readOnly, unit, defaultValue,
      uncontrolled, inputMode,
    } = this.props;

    const autofocus = shouldAutofocus(this.props.autofocus, this.props.mobileAutofocus);

    const optionlCoreInputParams = {};
    if (id) optionlCoreInputParams.id = id;
    if (name) optionlCoreInputParams.name = name;
    if (placeholder) optionlCoreInputParams.placeholder = placeholder;
    if (autoComplete) optionlCoreInputParams.autoComplete = autoComplete;
    if (inputMode) optionlCoreInputParams.inputMode = inputMode;

    // Only the credential screens pass a real autocomplete token — everything else is
    // patient data, which password managers should not offer to fill.
    const isCredentialInput = type === 'password' || (autoComplete && autoComplete !== 'off' && autoComplete !== 'false');
    const autofillParams = isCredentialInput ? {} : ignoreAutofillProps;

    let inputClasses = '';
    if (value) inputClasses += ' hasContent';
    if (withoutBorder) inputClasses += ' withoutBorder';

    const inputElement = !inputRenderer ? (
      <FormInput
        label={label}
        required={required}
        htmlFor={id}
        onLabelClick={this.handleLabelClick}
        error={error}
        hint={hint}
        optional={optional}
        className={className}
        labelClassName={labelClassName}
        unit={unit}
      >
        {type === 'currency' && this.renderCurrencyInput(autofocus)}
        {type !== 'currency' &&
        <input
          {...optionlCoreInputParams}
          ref={input => { this.input = input; }}
          className={inputClasses}
          type={type || 'text'}
          {...wrapValue(defaultValue, value, uncontrolled)}
          onBlur={this.onBlur.bind(this)}
          onClick={this.handleInputClick}
          onChange={this.onChange.bind(this)}
          onKeyUp={this.onKeyUp.bind(this)}
          disabled={disabled || forceDisabled}
          readOnly={readOnly}
          autoFocus={autofocus}
          autoCorrect="false"
          autoCapitalize="false"
          spellCheck={false}
          {...autofillParams}
        />
        }
      </FormInput>
    ) : (
      <FormInput
        label={label}
        required={required}
        htmlFor={id}
        onLabelClick={this.handleLabelClick}
        error={error}
        hint={hint}
        optional={optional}
        className={className}
        labelClassName={labelClassName}
        unit={unit}
      >
        {map(inputRenderer, (inputEl: any, key: any) => (
          <div className={inputEl.innerCssClasses} key={key}>
            {type === 'currency' && this.renderCurrencyInput(autofocus)}
            {type !== 'currency' &&
            <input
              {...omit(inputEl, ['labelElement', 'innerCssClasses', 'node'])}
              ref={input => { this.input = input; }}
              type={type || 'text'}
              onBlur={this.onBlur.bind(this)}
              onChange={this.onChange.bind(this)}
              onKeyUp={this.onKeyUp.bind(this)}
              onClick={this.handleInputClick}
              readOnly={readOnly}
              disabled={disabled || forceDisabled}
              autoFocus={autofocus}
              autoCorrect="false"
              autoCapitalize="false"
              spellCheck={false}
              {...autofillParams}
            />
            }
            {inputEl.node}
          </div>
        ))}
      </FormInput>
    );

    return inputElement;
  }

  input: ?HTMLInputElement;

  handleLabelClick = () => {
    if (this.input) {
      this.input.focus();
    }
  };

  handleInputClick = (e: Object) => {
    if (this.props.onClick) {
      this.props.onClick(e);
    }
  };

  onChange = (event: SyntheticKeyboardEvent) => {
    const { name, onChange, onCheck } = this.props;
    const { value } = event.target;
    const data = { name, value };

    if (onChange) {
      onChange(data);
    } else if (onCheck) {
      onCheck(data);
    }
  };

  handleOnChangeCurrency = (event: SyntheticKeyboardEvent, maskedvalue: String, floatvalue: Number) => {
    const { name, onChange } = this.props;
    const data = { name, value: floatvalue };
    if (isFunction(onChange)) {
      onChange(data);
    }
  };

  onBlur = (event: SyntheticKeyboardEvent) => {
    const { name, onBlur } = this.props;
    const { value } = event.target;
    const data = { name, value };
    if (onBlur) {
      onBlur(data);
    }
  };

  onKeyUp = (event: SyntheticKeyboardEvent) => {
    const { name, onKeyUp } = this.props;
    const { value } = event.target;
    if (onKeyUp) {
      onKeyUp({ name, value });
    }
  };
}
